import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { 
  Globe, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  ArrowUpDown,
  ExternalLink,
  Settings,
  Wifi,
  WifiOff,
  Sparkles,
  BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// OTA Platform colors
const OTA_COLORS = {
  'booking.com': 'bg-blue-600',
  'booking': 'bg-blue-600',
  'expedia': 'bg-yellow-500',
  'airbnb': 'bg-rose-500',
  'agoda': 'bg-purple-600',
  'hotels.com': 'bg-red-600',
  'hotels': 'bg-red-600',
  'makemytrip': 'bg-red-600',
  'goibibo': 'bg-sage-500',
  'hostelworld': 'bg-sage-600',
  'tripadvisor': 'bg-emerald-600',
  'vrbo': 'bg-blue-700',
  'google': 'bg-blue-500',
  'direct': 'bg-emerald-700',
  'unknown': 'bg-stone-500'
};

export default function OTAIntegration() {
  const [otaStatus, setOtaStatus] = useState(null);
  const [otaBookings, setOtaBookings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [setupModalOpen, setSetupModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, bookingsRes, logsRes] = await Promise.all([
        api.get('/ota/status'),
        api.get('/ota/bookings'),
        api.get('/ota/sync-logs')
      ]);

      setOtaStatus(statusRes.data);
      setOtaBookings(bookingsRes.data.data || []);
      setSyncLogs(logsRes.data.data || []);
    } catch (error) {
      // silently handled — OTA data is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fetchOTABookings = async () => {
    setSyncing(true);
    try {
      const response = await api.get('/ota/fetch-bookings');
      
      if (response.data.success) {
        toast.success(`Fetched ${response.data.fetched} bookings from OTAs`);
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch OTA bookings');
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getOTABadge = (otaName) => {
    const normalizedName = otaName?.toLowerCase() || 'unknown';
    const color = OTA_COLORS[normalizedName] || OTA_COLORS['unknown'];
    const displayName = otaName?.charAt(0).toUpperCase() + otaName?.slice(1) || 'Unknown';
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white ${color}`}>
        {displayName}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
      </div>
    );
  }

  return (
    <div data-testid="ota-integration-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">OTA Channels</h1>
          <p className="text-stone-500 mt-1">Manage reservations from Booking.com, Airbnb, Expedia & more</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setSetupModalOpen(true)}
            className="border-stone-300"
            data-testid="setup-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Setup
          </Button>
          <Button
            onClick={fetchOTABookings}
            disabled={syncing || !otaStatus?.is_configured}
            className="bg-sage-700 hover:bg-sage-800 text-white"
            data-testid="sync-bookings-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Fetch Bookings'}
          </Button>
        </div>
      </div>

      {/* Connection Status Card */}
      <div className="bg-white border border-stone-200 rounded-md p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-md ${otaStatus?.is_configured ? 'bg-emerald-50' : 'bg-stone-100'}`}>
              {otaStatus?.is_configured ? (
                <Wifi className="w-6 h-6 text-emerald-700" />
              ) : (
                <WifiOff className="w-6 h-6 text-stone-400" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-stone-900 font-['Manrope']">Nobeds Channel Manager</h3>
                <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
                  <Sparkles className="w-3 h-3 mr-1" />
                  FREE
                </span>
              </div>
              <p className="text-sm text-stone-500">
                {otaStatus?.is_configured 
                  ? 'Connected - Sync with 30+ OTA platforms'
                  : 'Not configured - Sign up free at nobeds.com'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {otaStatus?.is_configured ? (
              <span className="flex items-center gap-1 text-sm text-emerald-700">
                <CheckCircle className="w-4 h-4" />
                Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-stone-500">
                <XCircle className="w-4 h-4" />
                Disconnected
              </span>
            )}
          </div>
        </div>
        
        {/* Connected OTAs */}
        <div className="mt-4 pt-4 border-t border-stone-100">
          <p className="text-sm text-stone-600 mb-3">Supported OTA Platforms:</p>
          <div className="flex flex-wrap gap-2">
            {otaStatus?.supported_otas?.map((ota) => (
              <span 
                key={ota.id}
                className="px-3 py-1 text-sm rounded-full text-white"
                style={{ backgroundColor: ota.color }}
              >
                {ota.name}
              </span>
            )) || ['Booking.com', 'Airbnb', 'Expedia', 'Agoda', 'Hotels.com', 'MakeMyTrip', 'Goibibo', 'And more...'].map((ota) => (
              <span 
                key={ota}
                className="px-3 py-1 bg-stone-100 text-stone-700 text-sm rounded-full"
              >
                {ota}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="bookings" className="w-full">
        <TabsList className="bg-stone-100 p-1 mb-6">
          <TabsTrigger 
            value="bookings" 
            className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6"
            data-testid="tab-ota-bookings"
          >
            <Globe className="w-4 h-4 mr-2" />
            OTA Bookings ({otaBookings.length})
          </TabsTrigger>
          <TabsTrigger 
            value="sync" 
            className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6"
            data-testid="tab-sync-logs"
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            Sync Logs
          </TabsTrigger>
        </TabsList>

        {/* OTA Bookings Tab */}
        <TabsContent value="bookings">
          <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
            {otaBookings.length === 0 ? (
              <div className="p-12 text-center">
                <Globe className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p className="text-stone-500 mb-2">No OTA bookings yet</p>
                <p className="text-sm text-stone-400">
                  {otaStatus?.is_configured 
                    ? 'Click "Fetch Bookings" to pull reservations from connected OTAs'
                    : 'Configure Nobeds API to start receiving OTA bookings'
                  }
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50 hover:bg-stone-50">
                    <TableHead className="font-semibold text-stone-700">OTA Source</TableHead>
                    <TableHead className="font-semibold text-stone-700">Guest</TableHead>
                    <TableHead className="font-semibold text-stone-700">Check-in</TableHead>
                    <TableHead className="font-semibold text-stone-700">Check-out</TableHead>
                    <TableHead className="font-semibold text-stone-700">Amount</TableHead>
                    <TableHead className="font-semibold text-stone-700">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {otaBookings.map((booking, index) => (
                    <TableRow 
                      key={booking.id}
                      className="table-row-hover animate-fade-in"
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`ota-booking-row-${booking.id}`}
                    >
                      <TableCell>{getOTABadge(booking.ota_name)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-stone-900">{booking.guest_name || 'Guest'}</p>
                          <p className="text-xs text-stone-500">{booking.guest_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-600">{formatDate(booking.check_in_date)}</TableCell>
                      <TableCell className="text-stone-600">{formatDate(booking.check_out_date)}</TableCell>
                      <TableCell className="font-medium text-stone-900">{formatCurrency(booking.total_amount)}</TableCell>
                      <TableCell>
                        <span className={`status-badge status-${booking.status}`}>
                          {booking.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Sync Logs Tab */}
        <TabsContent value="sync">
          <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
            {syncLogs.length === 0 ? (
              <div className="p-12 text-center">
                <ArrowUpDown className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p className="text-stone-500">No sync logs yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50 hover:bg-stone-50">
                    <TableHead className="font-semibold text-stone-700">Time</TableHead>
                    <TableHead className="font-semibold text-stone-700">Type</TableHead>
                    <TableHead className="font-semibold text-stone-700">Records</TableHead>
                    <TableHead className="font-semibold text-stone-700">Status</TableHead>
                    <TableHead className="font-semibold text-stone-700">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncLogs.map((log, index) => (
                    <TableRow 
                      key={log.id}
                      className="table-row-hover"
                      data-testid={`sync-log-row-${log.id}`}
                    >
                      <TableCell className="text-stone-600 text-sm">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell className="capitalize text-stone-600">{log.sync_type?.replace('_', ' ')}</TableCell>
                      <TableCell className="text-stone-600">{log.records}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-sm ${
                          log.status === 'success' ? 'text-emerald-700' : 
                          log.status === 'error' ? 'text-red-600' : 'text-amber-600'
                        }`}>
                          {log.status === 'success' ? <CheckCircle className="w-4 h-4" /> :
                           log.status === 'error' ? <XCircle className="w-4 h-4" /> :
                           <AlertCircle className="w-4 h-4" />}
                          {log.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-stone-500 text-sm max-w-[200px] truncate">
                        {log.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Setup Modal */}
      <Dialog open={setupModalOpen} onOpenChange={setSetupModalOpen}>
        <DialogContent className="bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              Nobeds Setup
              <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
                FREE
              </span>
            </DialogTitle>
            <DialogDescription>
              Connect to Nobeds - a free channel manager for 30+ OTA platforms
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck className="w-5 h-5 text-emerald-700" />
                <h4 className="font-semibold text-emerald-900">Why Nobeds?</h4>
              </div>
              <ul className="text-sm text-emerald-800 space-y-1 list-disc list-inside">
                <li>100% FREE channel manager</li>
                <li>Connects to Booking.com, Airbnb, Expedia, Agoda & more</li>
                <li>Real-time sync prevents double bookings</li>
                <li>One calendar for all OTA reservations</li>
              </ul>
            </div>

            <div className="bg-stone-50 rounded-md p-4">
              <h4 className="font-semibold text-stone-900 mb-2">Setup Steps</h4>
              <ol className="text-sm text-stone-600 space-y-2 list-decimal list-inside">
                <li>Sign up free at <a href="https://nobeds.com" target="_blank" rel="noopener noreferrer" className="text-sage-700 hover:underline font-medium">nobeds.com</a></li>
                <li>Add your property in Nobeds dashboard</li>
                <li>Connect your OTA accounts (Booking.com, Airbnb, etc.)</li>
                <li>Go to Features → API → Generate API token</li>
                <li>Add the token to your environment</li>
              </ol>
            </div>

            <div className="space-y-3">
              <Label>Environment Variable</Label>
              <div className="bg-stone-900 text-stone-100 rounded-md p-4 font-mono text-sm">
                <p>NOBEDS_API_TOKEN=your_api_token_here</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="font-semibold text-blue-900 mb-1 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Supported Platforms
              </h4>
              <p className="text-sm text-blue-800">
                Booking.com, Airbnb, Expedia, Hotels.com, Agoda, MakeMyTrip, Goibibo, 
                Hostelworld, TripAdvisor, VRBO, Google Hotels, and more
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setSetupModalOpen(false)}>
                Close
              </Button>
              <a 
                href="https://nobeds.com" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-sage-700 hover:bg-sage-800 text-white">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Sign Up Free
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
